import type { FieldMeta, FieldUidt } from '../types/resource';

/**
 * Options for a SingleSelect / MultiSelect. Materialised from the
 * server's `edit.colOptions` array (each entry is
 * `{title, color?}` — the staging shape).
 */
export interface SelectOption {
  value: string;
  label: string;
  color?: string;
}

/**
 * Extract select options from a field. Handles both the current wire
 * shape (`edit.colOptions: [{title, color?}]`) and the older nested
 * shape (`colOptions.options: [...]`) that some other endpoints in
 * the mis-api still use.
 */
export function selectOptionsFor(field: FieldMeta): SelectOption[] {
  const raw = field.edit?.colOptions ?? extractLegacyOptions(field);
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry): SelectOption | null => {
      if (typeof entry === 'string') return { value: entry, label: entry };
      if (!entry || typeof entry !== 'object') return null;
      const obj = entry as Record<string, unknown>;
      const value = (obj.value ?? obj.title ?? '').toString();
      if (value === '') return null;
      const label = (obj.label ?? obj.title ?? value).toString();
      const color = typeof obj.color === 'string' ? obj.color : undefined;
      return { value, label, color };
    })
    .filter((o): o is SelectOption => o !== null);
}

function extractLegacyOptions(field: FieldMeta): unknown[] | undefined {
  const legacy = (field as { colOptions?: unknown }).colOptions;
  if (legacy && typeof legacy === 'object' && 'options' in legacy) {
    const options = (legacy as { options: unknown }).options;
    if (Array.isArray(options)) return options;
  }
  if (Array.isArray(legacy)) return legacy;
  return undefined;
}

/**
 * Effective widget for rendering / editing this field.
 *
 * Priority:
 *   1. `edit.uidt` — the explicit widget hint the server chose.
 *   2. Inference from the storage type when the field is not
 *      editable (grid rendering falls through here).
 *
 * Returns `undefined` for fields we can't classify — callers fall
 * back to plain string display.
 */
export function effectiveUidt(field: FieldMeta): FieldUidt | undefined {
  if (field.edit?.uidt) return field.edit.uidt;
  switch (field.type) {
    case 'integer':
    case 'decimal':
      return 'Number';
    case 'date':
      return 'Date';
    case 'datetime':
      return 'DateTime';
    case 'boolean':
      return 'Checkbox';
    case 'enum':
      return 'SingleSelect';
    case 'text':
      return 'SingleLineText';
    default:
      return undefined;
  }
}

/**
 * Convert a MultiSelect value to a normalised string[] regardless of
 * whether the server sends `"a,b,c"`, `["a","b","c"]`, or an empty
 * marker (`null` / `""`).
 */
export function toMultiSelectValues(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v)).filter((s) => s !== '');
  if (typeof value === 'string' && value !== '') return value.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
}

/**
 * Serialise a MultiSelect back to the wire format the backend used
 * — CSV string. Matches the pattern from the mobile-form update
 * example (`"irrigation_source": "Canal,Tubewell"`).
 */
export function fromMultiSelectValues(values: string[]): string {
  return values.filter((v) => v !== '').join(',');
}

/**
 * Format a stored value for display in the read-only grid. Falls
 * back to string coercion for uidts we don't specifically handle,
 * and returns an em-dash placeholder for null/undefined/empty.
 */
export function formatFieldValue(field: FieldMeta, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';

  const uidt = effectiveUidt(field);
  switch (uidt) {
    case 'Date':
      return formatDate(value);
    case 'DateTime':
    case 'CreatedTime':
    case 'LastModifiedTime':
      return formatDateTime(value);
    case 'Checkbox':
      return value === true || value === 'true' || value === 1 ? 'Yes' : 'No';
    case 'MultiSelect':
      return toMultiSelectValues(value).join(', ') || '—';
    case 'SingleSelect':
      return String(value);
    case 'Number':
    case 'Decimal':
    case 'Currency':
    case 'Percent':
    case 'Rating':
      if (typeof value === 'number') return formatNumber(value);
      return String(value);
    case 'LinkToAnotherRecord':
      return formatLinkedRecord(value);
    case 'Attachment':
      return formatAttachmentCount(value);
    default:
      if (value && typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        if (typeof obj.name === 'string') return obj.name;
        if (typeof obj.title === 'string') return obj.title;
        return JSON.stringify(value);
      }
      return String(value);
  }
}

/**
 * ISO-8601 date → `dd MMM yyyy`. Anything that doesn't parse falls
 * through to the raw string so we don't lose data.
 */
function formatDate(value: unknown): string {
  const raw = String(value);
  const asIso = raw.length >= 10 ? raw.slice(0, 10) : raw;
  const d = new Date(`${asIso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function formatDateTime(value: unknown): string {
  const raw = String(value);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  // Localised, but don't force decimals — an int stays an int.
  return n.toLocaleString('en-IN', { maximumFractionDigits: 4 });
}

function formatLinkedRecord(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((v) => formatLinkedRecord(v)).filter(Boolean).join(', ') || '—';
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof obj.name === 'string') return obj.name;
    if (typeof obj.title === 'string') return obj.title;
    if (typeof obj.id === 'string' || typeof obj.id === 'number') return `#${obj.id}`;
  }
  return String(value);
}

function formatAttachmentCount(value: unknown): string {
  if (Array.isArray(value)) {
    return value.length === 1 ? '1 file' : `${value.length} files`;
  }
  if (typeof value === 'string') return value ? '1 file' : '—';
  return '—';
}

/**
 * Coerce an editor's raw form-state value back to the wire format
 * the backend expects for the given uidt. This is the inverse of
 * `formatFieldValue` for display, aligned with what mobile forms
 * send on their PATCH.
 */
export function coerceFieldValueForWire(field: FieldMeta, value: unknown): unknown {
  if (value === '' || value === undefined) return null;

  const uidt = effectiveUidt(field);
  switch (uidt) {
    case 'Number':
    case 'Decimal':
    case 'Currency':
    case 'Percent':
    case 'Rating': {
      if (value === null) return null;
      const n = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(n) ? n : null;
    }
    case 'Checkbox':
      if (typeof value === 'boolean') return value;
      if (value === 'true' || value === 1 || value === '1') return true;
      if (value === 'false' || value === 0 || value === '0') return false;
      return Boolean(value);
    case 'MultiSelect':
      if (Array.isArray(value)) return fromMultiSelectValues(value.map((v) => String(v)));
      return String(value ?? '');
    case 'Date':
      // Prefer `YYYY-MM-DD` — strip time if present so the wire format
      // matches what the mobile form uses (`"survey_date": "2025-05-20"`).
      if (typeof value === 'string' && value.length >= 10) return value.slice(0, 10);
      return value;
    default:
      return value;
  }
}
